import type {DragEndEvent} from '@dnd-kit/core';
import type {ReactElement} from 'react';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {
  isFieldEnabled,
  toggleField,
  removeCustomField,
  restoreCustomField,
  moveBoxInForm,
  updateFieldLabel,
  updateCustomFieldLabel,
  updateCustomFieldLabelOnCanvas,
  updateBirthdayFormat,
  updateFieldRequired,
  updateCustomFieldRequired,
  updateStandardFieldRequired,
  isStandardFieldRequired,
} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import {buildCustomBoxType} from '@FormMakerReact/components/postermywall-custom-box/postermywall-custom-box';
import {BIRTHDAY_INPUT_COMPONENT} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import {BIRTHDAY_FORMATS, PMW_CUSTOM_INPUT} from '@FormMakerReact/form-maker.types';
import type {BirthdayFormat} from '@FormMakerReact/form-maker.types';
import {AddCustomFieldButtonControl} from '@FormMakerReact/layout/form-maker-right-panel/controls/add-custom-field-button-control/add-custom-field-button-control';
import {FieldToggleRow} from './components/field-toggle-row/field-toggle-row';
import type {FieldRowConfig, FieldRowRenderArgs, FieldToggleListControlProps} from './field-toggle-list-control.types';
import {
  buildEnabledSet,
  buildInitialRowOrder,
  getCanvasBoxTypeOrder,
  isToggleableBoxType,
  mergeOrderFromCanvas,
  resolveRowConfig,
  resolveRowLabel,
  slotBoxAtRow,
  subscribeToCanvasMutations,
  toggleSetMember,
  translateRowIndexToFormChildIndex,
} from './field-toggle-list-control.library';
import styles from './field-toggle-list-control.module.scss';

export function FieldToggleListControl({hideSectionHeading}: FieldToggleListControlProps): ReactElement {
  const {editor, state, actions} = useContext(formMakerContext);

  const [enabledBoxTypes, setEnabledBoxTypes] = useState<ReadonlySet<string>>(() => buildEnabledSet(editor));

  const [rowOrder, setRowOrder] = useState<readonly string[]>(() => buildInitialRowOrder(editor, state.fieldsMeta));

  const rowConfigs = useMemo<FieldRowConfig[]>(() => rowOrder.map((bt) => resolveRowConfig(bt)).filter((c): c is FieldRowConfig => c !== null), [rowOrder]);

  const [openEditBoxTypes, setOpenEditBoxTypes] = useState<ReadonlySet<string>>(() => buildEnabledSet(editor));

  useEffect(() => {
    if (!editor) {
      return undefined;
    }
    const syncFromCanvas = (): void => {
      const nextEnabled = buildEnabledSet(editor);
      setEnabledBoxTypes(nextEnabled);
      setOpenEditBoxTypes(new Set(nextEnabled));
      setRowOrder((prev) => mergeOrderFromCanvas(prev, getCanvasBoxTypeOrder(editor), state.fieldsMeta));
    };
    syncFromCanvas();
    return subscribeToCanvasMutations(editor, syncFromCanvas);
  }, [editor, state.fieldsMeta]);

  const handleToggle = useCallback(
    (boxType: string): void => {
      if (!editor || !isToggleableBoxType(boxType)) {
        return;
      }
      const wasEnabled = isFieldEnabled(editor, boxType);
      setEnabledBoxTypes((prev) => toggleSetMember(prev, boxType, !wasEnabled));
      setOpenEditBoxTypes((prev) => toggleSetMember(prev, boxType, !wasEnabled));
      toggleField(editor, boxType);
      if (!wasEnabled) {
        slotBoxAtRow(editor, boxType, rowOrder);
      }
    },
    [editor, rowOrder]
  );

  const handleToggleCustomField = useCallback(
    (customFieldIndex: number): void => {
      if (!editor) {
        return;
      }
      const boxType = buildCustomBoxType(customFieldIndex);
      const wasEnabled = isFieldEnabled(editor, boxType);
      setEnabledBoxTypes((prev) => toggleSetMember(prev, boxType, !wasEnabled));
      setOpenEditBoxTypes((prev) => toggleSetMember(prev, boxType, !wasEnabled));
      if (wasEnabled) {
        removeCustomField(editor, customFieldIndex);
      } else {
        restoreCustomField(editor, customFieldIndex, state.fieldsMeta);
        slotBoxAtRow(editor, boxType, rowOrder);
      }
    },
    [editor, rowOrder, state.fieldsMeta]
  );

  const handleEditToggle = useCallback((boxType: string): void => {
    setOpenEditBoxTypes((prev) => toggleSetMember(prev, boxType, !prev.has(boxType)));
  }, []);

  const handleLabelChange = useCallback(
    (boxType: string, newLabel: string): void => {
      actions.setFieldsMeta((prev) => updateFieldLabel(prev, boxType, newLabel));
    },
    [actions]
  );

  const handleCustomLabelChange = useCallback(
    (customFieldIndex: number, newLabel: string): void => {
      if (editor) {
        updateCustomFieldLabelOnCanvas(editor, customFieldIndex, newLabel);
      }
      actions.setFieldsMeta((prev) => updateCustomFieldLabel(prev, customFieldIndex, newLabel));
    },
    [editor, actions]
  );

  const handleFormatChange = useCallback(
    (newFormat: BirthdayFormat): void => {
      if (!editor) {
        return;
      }
      actions.setFieldsMeta((prev) => updateBirthdayFormat(editor, prev, newFormat));
    },
    [editor, actions]
  );

  const handleRequiredChange = useCallback(
    (boxType: string, customFieldIndex: number | undefined, isRequired: boolean): void => {
      if (!editor) {
        return;
      }
      updateFieldRequired(editor, boxType, isRequired);
      if (customFieldIndex !== undefined) {
        actions.setFieldsMeta((prev) => updateCustomFieldRequired(prev, customFieldIndex, isRequired));
        return;
      }
      actions.setFieldsMeta((prev) => updateStandardFieldRequired(prev, boxType, isRequired));
    },
    [editor, actions]
  );

  const reorderRows = useCallback(
    (fromIndex: number, toIndex: number): void => {
      if (!editor || fromIndex === toIndex) {
        return;
      }
      if (fromIndex < 0 || fromIndex >= rowOrder.length) {
        return;
      }
      const sourceBoxType = rowOrder[fromIndex];
      if (isLockedRow(sourceBoxType)) {
        return;
      }
      const minTo = firstUnlockedRowIndex(rowOrder);
      const clampedTo = Math.max(minTo, Math.min(toIndex, rowOrder.length - 1));
      // Toggled-off rows reorder only locally; their next toggle-on lands them at this slot.
      if (isFieldEnabled(editor, sourceBoxType)) {
        const targetChildIndex = translateRowIndexToFormChildIndex(editor, clampedTo);
        moveBoxInForm(editor, sourceBoxType, targetChildIndex);
      }
      setRowOrder((prev) => arrayMove(prev.slice(), fromIndex, clampedTo));
    },
    [editor, rowOrder]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const {active, over} = event;
      if (!over || active.id === over.id) {
        return;
      }
      const fromIndex = rowOrder.indexOf(active.id.toString());
      const toIndex = rowOrder.indexOf(over.id.toString());
      if (fromIndex === -1 || toIndex === -1) {
        return;
      }
      reorderRows(fromIndex, toIndex);
    },
    [rowOrder, reorderRows]
  );

  const currentBirthdayFormat = useMemo((): BirthdayFormat => {
    const entry = state.fieldsMeta.find((f) => f.component === BIRTHDAY_INPUT_COMPONENT);
    return (entry?.format as BirthdayFormat | undefined) ?? BIRTHDAY_FORMATS.DMY;
  }, [state.fieldsMeta]);

  const getLabelForConfig = useCallback(
    (config: FieldRowConfig): string => {
      if (config.customFieldIndex === undefined) {
        return '';
      }
      const entry = state.fieldsMeta.find((f) => f.component === PMW_CUSTOM_INPUT && f.customFieldIndex === config.customFieldIndex);
      return entry?.label ?? '';
    },
    [state.fieldsMeta]
  );

  const getIsRequiredForConfig = useCallback(
    (config: FieldRowConfig): boolean => {
      if (config.customFieldIndex !== undefined) {
        const entry = state.fieldsMeta.find((f) => f.component === PMW_CUSTOM_INPUT && f.customFieldIndex === config.customFieldIndex);
        return entry?.required ?? false;
      }
      if (!editor) {
        return false;
      }
      return isStandardFieldRequired(editor, config.boxType);
    },
    [state.fieldsMeta, editor]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {activationConstraint: {distance: 8}}),
    useSensor(TouchSensor, {activationConstraint: {delay: 125, tolerance: 5}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const sortableIds = useMemo((): string[] => rowOrder.slice(), [rowOrder]);

  return (
    <div>
      {renderSectionHeading(hideSectionHeading)}
      <div className={styles.list}>
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {rowConfigs.map((config, rowIndex) =>
              renderFieldRow({
                config,
                rowIndex,
                totalRows: rowOrder.length,
                enabledBoxTypes,
                onToggle: handleToggle,
                onToggleCustomField: handleToggleCustomField,
                onEditToggle: handleEditToggle,
                onLabelChange: handleLabelChange,
                onCustomLabelChange: handleCustomLabelChange,
                onFormatChange: handleFormatChange,
                onRequiredChange: handleRequiredChange,
                reorderRows,
                openEditBoxTypes,
                currentBirthdayFormat,
                getLabelForConfig,
                getIsRequiredForConfig,
                fieldsMeta: state.fieldsMeta,
              })
            )}
          </SortableContext>
        </DndContext>
        <AddCustomFieldButtonControl fieldsMeta={state.fieldsMeta} />
      </div>
    </div>
  );
}

function buildToggleHandler(boxType: string, customFieldIndex: number | undefined, onToggle: (bt: string) => void, onToggleCustom: (idx: number) => void): () => void {
  if (customFieldIndex !== undefined) {
    return (): void => {
      onToggleCustom(customFieldIndex);
    };
  }
  return (): void => {
    onToggle(boxType);
  };
}

function buildRequiredChangeHandler(
  boxType: string,
  customFieldIndex: number | undefined,
  onRequiredChange: (boxType: string, customFieldIndex: number | undefined, isRequired: boolean) => void
): (isRequired: boolean) => void {
  return (isRequired: boolean): void => {
    onRequiredChange(boxType, customFieldIndex, isRequired);
  };
}

function buildLabelChangeHandler(
  boxType: string,
  customFieldIndex: number | undefined,
  onLabelChange: (bt: string, label: string) => void,
  onCustomLabelChange: (idx: number, label: string) => void
): (newLabel: string) => void {
  if (customFieldIndex !== undefined) {
    return (newLabel: string): void => {
      onCustomLabelChange(customFieldIndex, newLabel);
    };
  }
  return (newLabel: string): void => {
    onLabelChange(boxType, newLabel);
  };
}

function renderFieldRow(args: FieldRowRenderArgs): ReactElement {
  const {
    config,
    rowIndex,
    totalRows,
    enabledBoxTypes,
    onToggle,
    onToggleCustomField,
    onEditToggle,
    onLabelChange,
    onCustomLabelChange,
    onFormatChange,
    onRequiredChange,
    reorderRows,
    openEditBoxTypes,
    currentBirthdayFormat,
    getLabelForConfig,
    getIsRequiredForConfig,
    fieldsMeta,
  } = args;
  const {customFieldIndex} = config;
  const enabled = config.isLocked || enabledBoxTypes.has(config.boxType);
  const isEditOpen = openEditBoxTypes.has(config.boxType);
  const label = resolveRowLabel(config, fieldsMeta);

  const onToggleRow = buildToggleHandler(config.boxType, customFieldIndex, onToggle, onToggleCustomField);
  const onLabelChangeRow = buildLabelChangeHandler(config.boxType, customFieldIndex, onLabelChange, onCustomLabelChange);
  const onRequiredChangeRow = buildRequiredChangeHandler(config.boxType, customFieldIndex, onRequiredChange);
  const onMoveUp = (): void => {
    reorderRows(rowIndex, rowIndex - 1);
  };
  const onMoveDown = (): void => {
    reorderRows(rowIndex, rowIndex + 1);
  };

  return (
    <FieldToggleRow
      key={config.boxType}
      boxType={config.boxType}
      label={label}
      iconClass={config.iconClass}
      isEnabled={enabled}
      isLocked={config.isLocked}
      isExpandable={config.isExpandable}
      isEditOpen={isEditOpen}
      editKind={config.editKind}
      editLabelValue={getLabelForConfig(config)}
      editBirthdayFormat={currentBirthdayFormat}
      isRequired={getIsRequiredForConfig(config)}
      onToggle={onToggleRow}
      onEditToggle={(): void => {
        onEditToggle(config.boxType);
      }}
      onLabelChange={onLabelChangeRow}
      onFormatChange={onFormatChange}
      onRequiredChange={onRequiredChangeRow}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      isFirstMovable={rowIndex === 0}
      isLastMovable={rowIndex >= totalRows - 1}
    />
  );
}

/**
 * Renders the "Info fields" section heading shown above the list in the desktop right panel.
 * Returns null in mobile bottom-sheet contexts where the sheet's PanelHeader already shows the same label.
 */
function renderSectionHeading(hideSectionHeading: boolean | undefined): ReactElement | null {
  if (hideSectionHeading) {
    return null;
  }
  return (
    <div className={styles.sectionHeader} role="heading" aria-level={3}>
      {window.i18next.t('pmwjs_info_fields')}
    </div>
  );
}

function isLockedRow(boxType: string): boolean {
  return resolveRowConfig(boxType)?.isLocked === true;
}

function firstUnlockedRowIndex(rowOrder: readonly string[]): number {
  for (let i = 0; i < rowOrder.length; i += 1) {
    if (!isLockedRow(rowOrder[i])) {
      return i;
    }
  }
  return 0;
}
